import { useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { AlertCircle, Box, ChevronLeft, LoaderCircle, Minus, Package, Plus, Search, X } from 'lucide-react';
import { useSharedQuote } from '../../context/QuoteContext';
import { InventoryItemImage } from '../../components/ui/InventoryItemImage';
import { LoadingSpinner } from '../../components/ui/LoadingSpinner';
import { ReadOnlyBanner } from '../../components/ui/ReadOnlyBanner';
import { QtyControls } from '../../components/quotes/QtyControls';
import { applyAddEquipment } from '../../utils/quoteInventory';
import { CasegoodsInventoryItem, OrgInventoryItem, Quote, QuoteResidentialRoom, QuoteType } from '../../types/admin';
import {
  DEFAULT_MATERIALS_RENTAL_DAYS,
  computeQuoteMaterials,
  formatUnitPricing,
  isPricedCategory,
  type MaterialCategory,
  type MaterialLine,
} from '../../lib/inventoryPricing';

// ── Category config ────────────────────────────────────────────────────────────
// Only "Packing Item" and "Equipment" items are materials. The rollup itself
// (aggregation, rental periods, line totals) lives in @iomi/shared's
// computeQuoteMaterials so the formula engine and quote versions use exactly
// the same numbers as this page.
//
// Where the quantities live:
//   • Commercial moves: the quote's casegoodsInventory lines. Some of those are
//     maintained by the inventory pages (an item's "Add Equipment" rules, modular
//     workstations), so a hand-edited quantity can be rewritten when inventory
//     changes. Materials added here that nothing else references are left alone.
//   • Residential moves: every room's items plus casegoodsInventory, which holds
//     materials added here (they have no room).

/** Categories shown, in display order. */
const MATERIAL_CATEGORIES: MaterialCategory[] = ['packing_item', 'equipment'];

const CATEGORY_LABEL: Record<MaterialCategory, string> = {
  packing_item: 'Packing Item',
  equipment: 'Equipment',
};

const CATEGORY_CLASSES: Record<MaterialCategory, string> = {
  packing_item: 'bg-amber-50 text-amber-600',
  equipment: 'bg-blue-50 text-blue-600',
};

function formatMoney(amount: number): string {
  return `$${amount.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

// ── Row model ──────────────────────────────────────────────────────────────────

interface MaterialRow extends MaterialLine {
  imageBase64?: string;
  hasImage?: boolean;
  description?: string;
  /** Another inventory item's "Add Equipment" rule maintains this quantity. */
  auto: boolean;
}

/** Group rows by category, preserving the configured category order. */
function groupByCategory(rows: MaterialRow[]): Array<{ category: MaterialCategory; rows: MaterialRow[] }> {
  return MATERIAL_CATEGORIES
    .map(category => ({ category, rows: rows.filter(r => r.category === category) }))
    .filter(group => group.rows.length > 0);
}

// ── Presentational pieces ──────────────────────────────────────────────────────

function ItemRow({ row, editable, onSetQty }: {
  row: MaterialRow;
  editable: boolean;
  onSetQty: (qty: number) => void;
}) {
  const unitLabel = formatUnitPricing(row.unitCost, row.rentalDays);
  const { periods, lineTotal: cost } = row;

  return (
    <li
      className="flex items-center gap-3 bg-white border border-stone rounded-xl px-3 py-3 shadow-brand-sm"
      data-testid={`material-${row.itemId}`}
    >
      <InventoryItemImage
        item={{ id: row.itemId, imageBase64: row.imageBase64, hasImage: row.hasImage }}
        alt={row.itemName}
        wrapperClassName="shrink-0"
        className="w-10 h-10 rounded-lg object-cover border border-stone"
        fallback={(
          <div className="w-10 h-10 rounded-lg bg-plum/10 flex items-center justify-center">
            <Box size={16} className="text-plum" />
          </div>
        )}
      />
      <div className="flex-1 min-w-0">
        <p className="text-small font-medium text-midnight truncate flex items-center gap-1.5">
          <span className="truncate">{row.itemName}</span>
          {row.auto && (
            <span
              className="shrink-0 text-[9px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded bg-mist text-slate"
              title="Kept in step with another inventory item's Add Equipment rule. Changing that item's quantity rewrites this one."
            >
              Auto
            </span>
          )}
        </p>
        {row.description && (
          <p className="text-[11px] text-slate leading-tight truncate">{row.description}</p>
        )}
        <p className="text-[12px] text-slate mt-0.5 font-mono" data-testid="unit-cost">
          {unitLabel ? (
            <>
              {unitLabel}
              {periods > 1 && <span className="text-slate"> · ×{periods} periods</span>}
            </>
          ) : (
            <span className="font-body">No cost set</span>
          )}
        </p>
      </div>
      <div className="shrink-0 flex flex-col items-end gap-1">
        {editable ? (
          <QtyControls
            quantity={row.quantity}
            onIncrement={() => onSetQty(row.quantity + 1)}
            onDecrement={() => onSetQty(row.quantity - 1)}
            onDirectEdit={onSetQty}
          />
        ) : (
          <span className="min-w-[2.5rem] text-center text-small font-bold text-midnight bg-mist rounded-lg px-2.5 py-1">
            ×{row.quantity}
          </span>
        )}
        <span className="text-small font-bold text-midnight font-mono" data-testid="line-total">
          {cost != null ? formatMoney(cost) : '—'}
        </span>
      </div>
    </li>
  );
}

function CategorySection({ category, rows, editable, onSetQty }: {
  category: MaterialCategory;
  rows: MaterialRow[];
  editable: boolean;
  onSetQty: (row: MaterialRow, qty: number) => void;
}) {
  return (
    <section>
      <h3 className="text-[12px] font-bold uppercase tracking-wider text-slate mb-2">
        {CATEGORY_LABEL[category]}
      </h3>
      <ul className="flex flex-col gap-2">
        {rows.map(row => (
          <ItemRow key={row.itemId} row={row} editable={editable} onSetQty={qty => onSetQty(row, qty)} />
        ))}
      </ul>
    </section>
  );
}

/** A named group (e.g. "Casegoods" / "Modular") that contains category subsections. */
function InventoryGroup({ title, rows, editable, onSetQty }: {
  title: string;
  rows: MaterialRow[];
  editable: boolean;
  onSetQty: (row: MaterialRow, qty: number) => void;
}) {
  const groups = groupByCategory(rows);
  if (groups.length === 0) return null;
  return (
    <div className="flex flex-col gap-5">
      <h2 className="font-display text-h3 text-midnight">{title}</h2>
      {groups.map(group => (
        <CategorySection key={group.category} category={group.category} rows={group.rows} editable={editable} onSetQty={onSetQty} />
      ))}
    </div>
  );
}

/** "Total Materials" summary. Shown above and below the list so it's visible either way. */
function TotalsCard({ total, priced, unpriced, testId }: { total: number; priced: number; unpriced: number; testId: string }) {
  return (
    <div className="flex items-center justify-between gap-3 bg-white border border-stone rounded-xl px-4 py-3 shadow-brand-sm">
      <div className="min-w-0">
        <p className="text-[11px] font-bold uppercase tracking-widest text-slate">Total Materials</p>
        <p className="text-[12px] text-slate leading-snug">
          {priced} priced item{priced !== 1 ? 's' : ''}
          {unpriced > 0 && ` · ${unpriced} without a cost`}
        </p>
      </div>
      <p className="shrink-0 font-mono text-h3 font-semibold text-midnight" data-testid={testId}>
        {formatMoney(total)}
      </p>
    </div>
  );
}

/** Whole-day rental length for the move. Saved on blur / Enter / stepper tap. */
function RentalLengthField({ value, onCommit, saving, error, disabled }: {
  value: number;
  onCommit: (days: number) => void;
  saving: boolean;
  error: string;
  disabled: boolean;
}) {
  const [draft, setDraft] = useState(String(value));
  useEffect(() => { setDraft(String(value)); }, [value]);

  function commit(next: number) {
    const days = Math.max(1, Math.floor(next));
    setDraft(String(days));
    if (days !== value) onCommit(days);
  }

  function commitDraft() {
    const n = parseInt(draft, 10);
    if (!Number.isFinite(n)) { setDraft(String(value)); return; }
    commit(n);
  }

  const locked = saving || disabled;

  return (
    <div className="bg-white border border-stone rounded-xl px-4 py-3 shadow-brand-sm">
      <div className="flex items-center justify-between gap-3">
        <div className="min-w-0">
          <label htmlFor="materials-rental-days" className="block text-[11px] font-bold uppercase tracking-widest text-slate">
            Rental Length
          </label>
          <p className="text-[12px] text-slate leading-snug">
            Rented items are billed in whole periods for this many days.
          </p>
        </div>
        <div className="shrink-0 flex items-center gap-1">
          <button
            type="button"
            onClick={() => commit(value - 1)}
            disabled={locked || value <= 1}
            aria-label="Decrease rental length"
            className="h-11 w-11 rounded-lg border border-stone text-slate flex items-center justify-center
                       hover:border-plum hover:text-plum active:bg-mist disabled:opacity-40 disabled:cursor-not-allowed
                       transition-colors duration-150"
          >
            <Minus size={16} />
          </button>
          <div className="flex items-baseline gap-1">
            <input
              id="materials-rental-days"
              type="number"
              inputMode="numeric"
              min={1}
              step={1}
              value={draft}
              onChange={e => setDraft(e.target.value)}
              onBlur={commitDraft}
              onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); (e.target as HTMLInputElement).blur(); } }}
              disabled={locked}
              className="w-14 h-11 rounded-lg border border-stone bg-white text-center font-mono text-small font-bold text-midnight
                         focus:outline-none focus:border-plum focus:ring-2 focus:ring-plum/20 transition-colors duration-150
                         disabled:opacity-60"
            />
            <span className="text-[12px] text-slate">day{value !== 1 ? 's' : ''}</span>
          </div>
          <button
            type="button"
            onClick={() => commit(value + 1)}
            disabled={locked}
            aria-label="Increase rental length"
            className="h-11 w-11 rounded-lg border border-stone text-slate flex items-center justify-center
                       hover:border-plum hover:text-plum active:bg-mist disabled:opacity-40
                       transition-colors duration-150"
          >
            <Plus size={16} />
          </button>
        </div>
      </div>
      {error && <p className="mt-2 text-[12px] text-rose">{error}</p>}
    </div>
  );
}

// ── Add Materials sheet ────────────────────────────────────────────────────────
// Same shape as the Casegoods Inventory "Add Items" sheet, limited to packing
// items and equipment, with each item's pricing shown under its name.

function AddMaterialsSheet({ orgItems, quoteType, lockedPrices, current, onAdd, onSetQty, onClose }: {
  orgItems: OrgInventoryItem[];
  quoteType: QuoteType;
  /** Prices locked into this quote; shown instead of the catalog price for those items. */
  lockedPrices: Quote['materialPrices'];
  /** itemId → quantity already on the move. */
  current: Map<string, number>;
  onAdd: (item: OrgInventoryItem) => void;
  onSetQty: (item: OrgInventoryItem, qty: number) => void;
  onClose: () => void;
}) {
  const [search, setSearch] = useState('');

  const materials = orgItems
    .filter(i => isPricedCategory(i.residentialType) && (i.quoteType ?? 'commercial') === quoteType)
    .sort((a, b) => {
      const order = (c?: string) => (c === 'packing_item' ? 0 : 1);
      const diff = order(a.residentialType) - order(b.residentialType);
      return diff !== 0 ? diff : a.name.localeCompare(b.name);
    });

  const filtered = search.trim()
    ? materials.filter(i => i.name.toLowerCase().includes(search.trim().toLowerCase()))
    : materials;

  return (
    <div className="fixed inset-0 z-50 flex flex-col justify-end md:items-center md:justify-center">
      <div className="absolute inset-0 bg-midnight/50" onClick={onClose} aria-hidden="true" />
      <div
        className="relative bg-white rounded-t-2xl flex flex-col max-h-[85vh] md:rounded-2xl md:max-w-lg md:w-full md:max-h-[80vh]"
        role="dialog"
        aria-modal="true"
        aria-label="Add Materials"
      >
        {/* Header */}
        <div className="px-4 pt-5 pb-3 flex-shrink-0">
          <div className="w-10 h-1 bg-stone rounded-full mx-auto mb-4 md:hidden" />
          <div className="flex items-center justify-between mb-3">
            <h2 className="font-display text-h3 text-midnight">Add Materials</h2>
            <button
              type="button"
              onClick={onClose}
              className="w-8 h-8 flex items-center justify-center rounded-lg text-slate
                         hover:text-midnight hover:bg-mist transition-colors"
              aria-label="Close"
            >
              <X size={16} />
            </button>
          </div>

          <div className="relative">
            <div className="pointer-events-none absolute inset-y-0 left-3 flex items-center">
              <Search size={14} className="text-slate" />
            </div>
            <input
              type="search"
              placeholder="Search materials…"
              value={search}
              onChange={e => setSearch(e.target.value)}
              className="w-full h-10 pl-9 pr-3 rounded-lg border border-stone bg-mist
                         text-small text-midnight placeholder:text-slate
                         focus:outline-none focus:border-plum focus:ring-2 focus:ring-plum/20
                         transition-colors duration-150"
            />
          </div>
        </div>

        {/* Scrollable list */}
        <div className="flex-1 overflow-y-auto px-4 pb-4">
          {filtered.length === 0 && (
            <div className="flex flex-col items-center py-12 gap-2 text-center">
              <p className="text-small text-slate">
                No packing items or equipment found{search ? ` for "${search}"` : ''}.
              </p>
            </div>
          )}

          {filtered.length > 0 && (
            <ul className="flex flex-col gap-2">
              {filtered.map(item => {
                const qty = current.get(item.id);
                const added = qty !== undefined && qty > 0;
                const category = item.residentialType as MaterialCategory;
                const price = lockedPrices?.[item.id] ?? item;
                const pricing = formatUnitPricing(price.unitCost, price.rentalDays);

                return (
                  <li
                    key={item.id}
                    className={`flex gap-3 border rounded-xl px-3 py-2.5
                      ${added ? 'bg-plum/5 border-plum/20' : 'bg-white border-stone'}`}
                  >
                    <InventoryItemImage
                      item={item}
                      alt={item.name}
                      wrapperClassName="shrink-0 pt-0.5"
                      className="w-10 h-10 rounded-lg object-cover border border-stone"
                      fallback={(
                        <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${added ? 'bg-plum/10' : 'bg-mist'}`}>
                          <Box size={16} className={added ? 'text-plum' : 'text-slate'} />
                        </div>
                      )}
                    />

                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-3">
                        <div className="flex-1 min-w-0">
                          <p className="text-small text-midnight truncate font-medium">{item.name}</p>
                          <div className="flex items-center gap-2 mt-0.5">
                            <span className={`shrink-0 text-[10px] font-bold uppercase px-2 py-0.5 rounded-full ${CATEGORY_CLASSES[category] ?? 'bg-mist text-slate'}`}>
                              {CATEGORY_LABEL[category] ?? category}
                            </span>
                            <span className="text-[11px] text-slate font-mono truncate">
                              {pricing ?? 'No cost set'}
                            </span>
                          </div>
                        </div>

                        {added ? (
                          <QtyControls
                            quantity={qty}
                            onIncrement={() => onSetQty(item, qty + 1)}
                            onDecrement={() => onSetQty(item, qty - 1)}
                            onDirectEdit={n => onSetQty(item, n)}
                          />
                        ) : (
                          <button
                            type="button"
                            onClick={() => onAdd(item)}
                            className="shrink-0 w-8 h-8 flex items-center justify-center rounded-lg
                                       bg-plum text-white hover:bg-rose active:opacity-80
                                       transition-colors duration-100"
                            aria-label={`Add ${item.name}`}
                          >
                            <Plus size={16} />
                          </button>
                        )}
                      </div>
                      {item.description && (
                        <p className="mt-1.5 text-[11px] text-slate leading-tight">{item.description}</p>
                      )}
                    </div>
                  </li>
                );
              })}
            </ul>
          )}
        </div>

        {/* Done */}
        <div className="px-4 pt-2 pb-6 flex-shrink-0 border-t border-stone">
          <button
            type="button"
            onClick={onClose}
            className="w-full h-12 rounded-xl bg-plum text-white text-[15px] font-semibold
                       hover:bg-rose active:opacity-80 transition-colors duration-150"
          >
            Done
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Page ─────────────────────────────────────────────────────────────────────

export function MaterialsPage() {
  const { quoteId } = useParams<{ quoteId: string }>();
  const navigate = useNavigate();
  const { quote, loading, updateQuote, orgItems, orgItemsLoading: itemsLoading } = useSharedQuote();

  const [savingDays, setSavingDays] = useState(false);
  const [daysError, setDaysError] = useState('');
  const [showAddSheet, setShowAddSheet] = useState(false);

  // Local copies of the quantity sources, edited here and auto-saved with a
  // short debounce (same pattern as the Casegoods Inventory page).
  const [inventory, setInventory] = useState<CasegoodsInventoryItem[]>([]);
  const [rooms, setRooms] = useState<QuoteResidentialRoom[]>([]);
  const [initialized, setInitialized] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState('');
  // Bumped on every local edit; a save only clears "dirty" if no newer edit happened meanwhile.
  const editSeqRef = useRef(0);
  const dirtyRef = useRef(false);

  const isCommercial = quote?.type === 'commercial';
  const isDraft = quote?.quoteStatus === 'draft';

  useEffect(() => {
    if (quote && !initialized) {
      setInventory(quote.casegoodsInventory ?? []);
      setRooms(quote.residentialRooms ?? []);
      setInitialized(true);
    }
  }, [quote, initialized]);

  // Follow server-side rewrites (other pages, the formula engine) while nothing is pending here.
  useEffect(() => {
    if (!initialized || dirtyRef.current) return;
    setInventory(quote?.casegoodsInventory ?? []);
    setRooms(quote?.residentialRooms ?? []);
  }, [quote?.casegoodsInventory, quote?.residentialRooms, initialized]);

  useEffect(() => {
    if (!initialized || !dirtyRef.current || !isDraft) return;
    const seq = editSeqRef.current;
    const timer = setTimeout(() => {
      setSaving(true);
      setSaveError('');
      const patch = isCommercial
        ? { casegoodsInventory: inventory }
        : { casegoodsInventory: inventory, residentialRooms: rooms };
      updateQuote(patch)
        .then(() => {
          if (editSeqRef.current === seq) dirtyRef.current = false;
          setSaving(false);
        })
        .catch(err => {
          setSaveError(err instanceof Error ? err.message : 'Auto-save failed');
          setSaving(false);
        });
    }, 400);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [inventory, rooms, initialized, isDraft, isCommercial]);

  function markDirty() {
    dirtyRef.current = true;
    editSeqRef.current += 1;
  }

  /**
   * Set a material's quantity on the move (0 removes it).
   * Commercial: the casegoodsInventory line, with the item's own Add Equipment
   * rules applied like the Casegoods page does. Residential: room items are the
   * base; anything above the rooms' total goes to a room-less line, and a lower
   * total trims the rooms from the last one back.
   */
  function setMaterialQty(itemId: string, itemName: string, category: MaterialCategory, newQty: number) {
    const qty = Math.max(0, Math.floor(newQty));
    markDirty();

    const upsertLine = (prev: CasegoodsInventoryItem[], q: number): CasegoodsInventoryItem[] => {
      if (q <= 0) return prev.filter(i => i.itemId !== itemId);
      if (prev.some(i => i.itemId === itemId)) return prev.map(i => i.itemId === itemId ? { ...i, quantity: q } : i);
      return [...prev, { itemId, itemName, quantity: q, itemCategory: category }];
    };

    if (isCommercial) {
      setInventory(prev => {
        const oldQty = prev.find(i => i.itemId === itemId)?.quantity ?? 0;
        return applyAddEquipment(upsertLine(prev, qty), itemId, oldQty, qty, orgItems);
      });
      return;
    }

    const roomTotal = rooms.reduce((sum, room) => sum + room.items.filter(i => i.itemId === itemId).reduce((s, i) => s + i.quantity, 0), 0);
    if (qty >= roomTotal) {
      setInventory(prev => upsertLine(prev, qty - roomTotal));
      return;
    }
    setInventory(prev => upsertLine(prev, 0));
    let toRemove = roomTotal - qty;
    setRooms(prev => {
      const next = prev.map(room => ({ ...room, items: room.items.map(i => ({ ...i })) }));
      for (let r = next.length - 1; r >= 0 && toRemove > 0; r--) {
        for (let k = next[r].items.length - 1; k >= 0 && toRemove > 0; k--) {
          const item = next[r].items[k];
          if (item.itemId !== itemId) continue;
          const take = Math.min(item.quantity, toRemove);
          item.quantity -= take;
          toRemove -= take;
        }
        next[r].items = next[r].items.filter(i => i.quantity > 0);
      }
      return next;
    });
  }

  if (loading || itemsLoading || !initialized) {
    return (
      <div className="flex justify-center py-16">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  if (!quote) {
    return (
      <div className="px-4 pt-6 flex flex-col items-center py-20 gap-2 text-center">
        <p className="text-small text-rose font-semibold">Quote not found</p>
      </div>
    );
  }

  const jobRentalDays = quote.materialsRentalDays ?? DEFAULT_MATERIALS_RENTAL_DAYS;

  async function saveRentalDays(days: number) {
    setDaysError('');
    setSavingDays(true);
    try {
      await updateQuote({ materialsRentalDays: days });
    } catch (err) {
      setDaysError(err instanceof Error ? err.message : 'Failed to save rental length.');
    } finally {
      setSavingDays(false);
    }
  }

  const orgMap = new Map(orgItems.map(i => [i.id, i]));

  // Same rollup the formula engine uses for the "Materials" line on the quote,
  // computed from the local (possibly unsaved) quantities so edits show at once.
  const summary = computeQuoteMaterials({ ...quote, casegoodsInventory: inventory, residentialRooms: rooms }, orgItems);

  // Materials another line's Add Equipment rule maintains.
  const autoIds = new Set<string>();
  if (isCommercial) {
    for (const line of inventory) {
      for (const eq of orgMap.get(line.itemId)?.addEquipment ?? []) autoIds.add(eq.itemId);
    }
  }

  const allRows: MaterialRow[] = summary.lines.map(line => ({
    ...line,
    imageBase64: orgMap.get(line.itemId)?.imageBase64,
    hasImage: orgMap.get(line.itemId)?.hasImage,
    description: orgMap.get(line.itemId)?.description,
    auto: autoIds.has(line.itemId),
  }));

  // ── Commercial: split casegoods vs modular inventory ──────────────────────
  const casegoodsRows = isCommercial ? allRows.filter(r => orgMap.get(r.itemId)?.type === 'Casegoods') : [];
  const modularRows = isCommercial
    ? allRows.filter(r => { const t = orgMap.get(r.itemId)?.type; return t === 'Modular Component' || t === 'Modular Equipment'; })
    : [];
  // ── Residential: single grouped list ──────────────────────────────────────
  const residentialRows = isCommercial ? [] : allRows;

  const hasContent = allRows.length > 0;
  const totals = { total: summary.total, priced: summary.pricedCount, unpriced: summary.unpricedCount };
  const currentQty = new Map(summary.lines.map(l => [l.itemId, l.quantity]));
  const onSetQty = (row: MaterialRow, qty: number) => setMaterialQty(row.itemId, row.itemName, row.category, qty);

  return (
    <div className="px-4 pt-6 pb-10 md:max-w-3xl md:mx-auto">
      {/* Back — hidden on md+ (shell left panel provides navigation) */}
      <button
        type="button"
        onClick={() => navigate(`/moves/${quoteId}`)}
        className="md:hidden flex items-center gap-1 text-small text-slate hover:text-midnight mb-4
                   transition-colors duration-150 -ml-1"
      >
        <ChevronLeft size={16} />
        {quote.customerCompanyName}
      </button>

      <div className="flex items-center justify-between mb-5">
        <h1 className="font-display text-h1 text-midnight">Materials</h1>
        <div className="flex items-center gap-2">
          {saving && <LoaderCircle size={16} className="text-slate animate-spin shrink-0" />}
          {isDraft && (
            <button
              type="button"
              onClick={() => setShowAddSheet(true)}
              className="flex items-center gap-1.5 h-11 px-3 rounded-xl bg-plum text-white
                         text-[13px] font-semibold
                         hover:bg-rose active:opacity-80 transition-colors duration-150"
            >
              <Plus size={15} />
              Add
            </button>
          )}
        </div>
      </div>

      {!isDraft && <ReadOnlyBanner status={quote.quoteStatus} />}

      {saveError && (
        <div className="mb-4 flex items-start gap-2 px-4 py-3 rounded-xl bg-rose/10 border border-rose/30">
          <AlertCircle size={15} className="shrink-0 text-rose mt-0.5" />
          <p className="text-small text-rose">{saveError}</p>
        </div>
      )}

      {/* Rental length + running total — always visible at the top */}
      <div className="flex flex-col gap-3 mb-6">
        <RentalLengthField value={jobRentalDays} onCommit={saveRentalDays} saving={savingDays} error={daysError} disabled={!isDraft} />
        {hasContent && <TotalsCard {...totals} testId="materials-total-top" />}
      </div>

      {!hasContent ? (
        <div className="flex flex-col items-center py-20 gap-4 text-center">
          <div className="w-16 h-16 rounded-full bg-mist flex items-center justify-center">
            <Package size={28} className="text-slate" />
          </div>
          <div>
            <p className="text-small font-semibold text-midnight mb-1">No materials yet</p>
            <p className="text-[13px] text-slate leading-snug">
              Packing items and equipment added to this move will appear here.
              {isDraft && <> Tap <span className="font-semibold text-plum">Add</span> to pick some.</>}
            </p>
          </div>
        </div>
      ) : (
        <>
          {isCommercial ? (
            <div className="flex flex-col gap-8">
              <InventoryGroup title="Casegoods" rows={casegoodsRows} editable={isDraft} onSetQty={onSetQty} />
              <InventoryGroup title="Modular" rows={modularRows} editable={isDraft} onSetQty={onSetQty} />
            </div>
          ) : (
            <div className="flex flex-col gap-5">
              {groupByCategory(residentialRows).map(group => (
                <CategorySection key={group.category} category={group.category} rows={group.rows} editable={isDraft} onSetQty={onSetQty} />
              ))}
            </div>
          )}

          {/* Total again at the bottom */}
          <div className="mt-6">
            <TotalsCard {...totals} testId="materials-total-bottom" />
          </div>
        </>
      )}

      {showAddSheet && (
        <AddMaterialsSheet
          orgItems={orgItems}
          quoteType={quote.type ?? 'commercial'}
          lockedPrices={quote.materialPrices}
          current={currentQty}
          onAdd={item => setMaterialQty(item.id, item.name, item.residentialType as MaterialCategory, 1)}
          onSetQty={(item, qty) => setMaterialQty(item.id, item.name, item.residentialType as MaterialCategory, qty)}
          onClose={() => setShowAddSheet(false)}
        />
      )}
    </div>
  );
}
